import axios from 'axios'
import { API_PAYROLL, ENV } from '../../env'
import {
  RequestShareAccessResponse,
  ShareAccessRequest,
  ShareAccessResponse,
  ShareDecision,
  ShareDecisionResponse,
  ShareErrorCode,
  ShareRequestError
} from './types'
export * from './types'

/**
 * Acceso publico a desprendibles compartidos (contexto document-sharing).
 *
 * Usa una instancia propia de axios y NO HttpService a proposito: el
 * interceptor de HttpService desloguea al usuario ante un 401, y estos
 * endpoints responden 401 cuando el enlace magico expiro. En una pantalla
 * publica eso mandaria al empleado al login en vez de explicarle que su enlace
 * vencio. Tampoco necesitan Authorization ni cabeceras de payroll.
 */
const http = axios.create({
  baseURL: API_PAYROLL[ENV()](),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

const SHARES_PATH = 'public/documents/shares'

/* El backend responde { code } tanto en los 401/410 propios como en los errores de dominio */
const toShareError = (error: any): ShareRequestError => {
  const body = error?.response?.data
  const code = body?.code
  const known: ShareErrorCode[] = [
    'INVALID_SHARE_LINK',
    'INVALID_ACCESS_TOKEN',
    'ACCESS_TOKEN_EXPIRED',
    'ShareAccessRequestTooSoon',
    'DocumentShareNotFound',
    'DocumentShareNotAccessible',
    'InvalidShareApprovalTransition'
  ]
  const retryAfterSeconds = body?.params?.retryAfterSeconds
  return new ShareRequestError(
    known.includes(code) ? (code as ShareErrorCode) : 'UNKNOWN',
    typeof retryAfterSeconds === 'number' ? retryAfterSeconds : undefined
  )
}

/**
 * Pide que se envie por correo el enlace magico de acceso, con el `data` del
 * primer correo o con un `access` ya vencido.
 */
export async function requestShareAccess(
  link: ShareAccessRequest
): Promise<RequestShareAccessResponse> {
  try {
    const response = await http.post<RequestShareAccessResponse>(
      `${SHARES_PATH}/request-access`,
      link
    )
    return response.data
  } catch (error: any) {
    throw toShareError(error)
  }
}

/**
 * Canjea el token magico por la URL prefirmada del desprendible.
 * El `access` es el token que viaja en el enlace del correo.
 */
export async function accessShare(
  access: string
): Promise<ShareAccessResponse> {
  try {
    const response = await http.post<ShareAccessResponse>(
      `${SHARES_PATH}/access`,
      { access }
    )
    return response.data
  } catch (error: any) {
    throw toShareError(error)
  }
}

/* Aprueba o rechaza el desprendible con el mismo token magico */
export async function decideShare(
  access: string,
  decision: ShareDecision
): Promise<ShareDecisionResponse> {
  try {
    const response = await http.post<ShareDecisionResponse>(
      `${SHARES_PATH}/decision`,
      { access, decision }
    )
    return response.data
  } catch (error: any) {
    throw toShareError(error)
  }
}
